import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, MailX, XCircle } from "lucide-react";
import api from "../lib/api";
import BrandIcon from "../components/app/BrandIcon";

const SUPPORTED = ["it", "en", "de", "fr", "es", "pl"];

function useSubscription(u, tok) {
  const [state, setState] = useState({ status: "loading", email: "" });
  const qs = { params: { u, t: tok } };
  useEffect(() => {
    if (!u || !tok) { setState({ status: "invalid" }); return; }
    api.get("/email/unsubscribe", qs)
      .then(({ data }) => setState({ status: data.unsubscribed ? "done" : "ask", email: data.email }))
      .catch(() => setState({ status: "invalid" }));
  }, [u, tok]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (path, next) => {
    setState((s) => ({ ...s, busy: true, error: false }));
    try { const { data } = await api.post(path, null, qs); setState({ status: next, email: data.email }); }
    catch { setState((s) => ({ ...s, busy: false, error: true })); }
  };
  return { state, unsubscribe: () => act("/email/unsubscribe", "done"), resubscribe: () => act("/email/resubscribe", "resubscribed") };
}

function Panel({ icon: Icon, tone, title, body, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className={`w-16 h-16 mx-auto rounded-full border flex items-center justify-center mb-5 ${tone}`}><Icon size={30} /></div>
      <h1 className="font-display font-bold text-2xl text-slate-100">{title}</h1>
      {body && <p className="text-slate-400 mt-3 leading-relaxed">{body}</p>}
      <div className="mt-8 flex flex-col items-center gap-3">{children}</div>
    </div>
  );
}

export default function UnsubscribePage() {
  const { i18n } = useTranslation();
  const [params] = useSearchParams();
  const hl = params.get("hl");
  const t = i18n.getFixedT(SUPPORTED.includes(hl) ? hl : i18n.language);
  const { state, unsubscribe, resubscribe } = useSubscription(params.get("u"), params.get("t"));
  const { status, email, busy, error } = state;
  const primary = "btn-primary px-6 py-3 rounded-md text-sm font-semibold disabled:opacity-50";
  const home = <Link to="/" className="text-sm text-slate-400 hover:text-amber-300" data-testid="unsub-home">{t("unsubscribe.backHome")}</Link>;

  return (
    <div className="theme-swiss min-h-screen flex items-center justify-center bg-slate-900 p-6">
      <div className="w-full max-w-md text-center" data-testid="unsubscribe-page">
        <div className="flex items-center gap-3 mb-10 justify-center">
          <BrandIcon size={40} />
          <div className="font-display font-bold text-xl text-slate-100">MachinePilot</div>
        </div>
        {status === "loading" && <Loader2 size={40} className="text-amber-400 mx-auto animate-spin" data-testid="unsub-loading" />}
        {status === "invalid" && <Panel icon={XCircle} tone="bg-red-500/10 border-red-500/40 text-red-400" title={t("unsubscribe.invalidTitle")} body={t("unsubscribe.invalid")} testId="unsub-invalid">{home}</Panel>}
        {status === "ask" && (
          <Panel icon={MailX} tone="bg-amber-500/10 border-amber-500/40 text-amber-400" title={t("unsubscribe.title")} body={t("unsubscribe.question", { email })} testId="unsub-ask">
            <button onClick={unsubscribe} disabled={busy} className={primary} data-testid="unsub-confirm">{t("unsubscribe.confirm")}</button>
            {home}
          </Panel>
        )}
        {status === "done" && (
          <Panel icon={CheckCircle2} tone="bg-emerald-500/10 border-emerald-500/40 text-emerald-400" title={t("unsubscribe.doneTitle")} body={t("unsubscribe.doneBody", { email })} testId="unsub-done">
            <button onClick={resubscribe} disabled={busy} className="text-sm text-amber-400 hover:text-amber-300 disabled:opacity-50" data-testid="unsub-resubscribe">{t("unsubscribe.resubscribe")}</button>
            {home}
          </Panel>
        )}
        {status === "resubscribed" && <Panel icon={CheckCircle2} tone="bg-emerald-500/10 border-emerald-500/40 text-emerald-400" title={t("unsubscribe.resubscribedTitle")} body={t("unsubscribe.resubscribedBody", { email })} testId="unsub-resubscribed">{home}</Panel>}
        {error && <p className="text-sm text-red-300 mt-6" role="alert" data-testid="unsub-error">{t("unsubscribe.error")}</p>}
      </div>
    </div>
  );
}
